// components/AI/AIRouterSettings.js
// Toggle on/off untuk fitur Auto-detect Dashboard (AI Workspace mode Umum).
// Saat aktif, tiap pertanyaan di mode Umum memicu 1 panggilan klasifikasi
// ringan ke Groq (via AI Service, key rotasi terpisah dari provider chat
// utama) SEBELUM panggilan jawaban biasa — jadi ada biaya/latensi tambahan
// kecil per pertanyaan. Toggle ini untuk admin yang ingin mematikannya sama
// sekali (mis. tidak ingin panggilan LLM ekstra, cukup fallback kata kunci,
// atau tidak ingin auto-detect sama sekali).
import React, { useState, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { Loader2, Zap, ZapOff, Info } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';

export default function AIRouterSettings() {
  const {
    lang,
    t
  } = useLang();
  const id = lang !== 'en';
  const [enabled, setEnabled] = useState(null); // null = loading
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    let alive = true;
    api.get('/api/ai/router-config')
      .then(r => { if (alive) setEnabled(!!r.data?.enabled); })
      .catch(e => { if (alive) setError(e.response?.data?.error || e.message); });
    return () => { alive = false; };
  }, []);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true); setError('');
    try {
      await api.put('/api/ai/router-config', { enabled: next });
      setEnabled(next);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  if (enabled === null && !error) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center">
        <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} />
        {t('aiRouter.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border border-slate-200 rounded-xl p-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h4 className="font-semibold text-sm text-slate-800 flex items-center gap-1.5">
            {enabled ? <Zap style={{ width: 15, height: 15 }} className="text-emerald-500" /> : <ZapOff style={{ width: 15, height: 15 }} className="text-slate-400" />}
            {id ? 'Auto-detect Dashboard' : 'Auto-detect Dashboard'}
          </h4>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            {t('aiRouter.whenAiWorkspaceGeneralMode')}
          </p>
        </div>
        <button onClick={toggle} disabled={saving}
          role="switch" aria-checked={enabled}
          className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${enabled ? 'bg-emerald-500' : 'bg-slate-300'} disabled:opacity-50`}>
          <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>
      <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/60">
        <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
          <Info style={{ width: 12, height: 12 }} /> {t('aiRouter.howWorks')}
        </h5>
        <ol className="text-xs text-slate-600 space-y-1.5 list-decimal list-inside leading-relaxed">
          <li>{t('aiRouter.quickClassificationViaGroqSeparate')}</li>
          <li>{t('aiRouter.failsUncertainFallsBackKeyword')}</li>
          <li>{t('aiRouter.noMatchKeepsUsingCurrently')}</li>
        </ol>
        <p className="text-[11px] text-slate-400 mt-3">
          {t('aiRouter.turnOffIfDonT')}
        </p>
      </div>
      {error && <p className="text-xs text-rose-500">{error}</p>}
    </div>
  );
}
